import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  signal,
} from '@angular/core';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SynchronizationFormComponent } from '../synchronization-form/synchronization-form.component';
import {
  SynchronizationFormConfig,
  SYNCHRONIZATION_HEADER_ACTIONS,
  createSynchronizationFormFields,
  SYNCHRONIZATION_TABLE_SECTIONS,
} from '../../synchronization.config';  
import { SynchronizationItem } from '../../models/synchronization.model';
import { SynchronizationService } from '../../services/synchronization.service';
import { extractErrorMessage, getErrorSeverity, getErrorTitle } from '@shared/utils/error-handler.util';

@Component({
  selector: 'app-synchronization-edit',
  standalone: true,
  imports: [
    SynchronizationFormComponent,
    ToastModule,
    TranslateModule
  ],
  providers: [SynchronizationService, MessageService],
  templateUrl: './synchronization-edit.component.html',
  styleUrls: ['./synchronization-edit.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SynchronizationEditComponent implements OnInit, CanComponentDeactivate {
  @ViewChild('formWrapper') private readonly formWrapper?: SynchronizationFormComponent;

  config: SynchronizationFormConfig = {
    title: 'synchronization.form.edit.title',
    formFields: createSynchronizationFormFields(),
    headerActions: SYNCHRONIZATION_HEADER_ACTIONS, 
    tableSections: SYNCHRONIZATION_TABLE_SECTIONS,
    listRoute: '/stock/synchronization',
  };

  // Data signals
  synchronizationData = signal<SynchronizationItem | null>(null);
  loading = signal(false);

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private synchronizationService: SynchronizationService,
    private messageService: MessageService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadSynchronizationData();
  }

  canDeactivate(): boolean {
    return this.formWrapper?.canDeactivate() ?? true;
  }

  private loadSynchronizationData(): void {
    const synchronizationId = this.route.snapshot.queryParams['id'];

    if (!synchronizationId) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('synchronization.messages.error.title'),
        detail: this.translateService.instant('synchronization.messages.error.no_id_provided'),
        life: 3000
      });
      return;
    }

    this.loading.set(true);
    this.synchronizationService.getSynchronizationById(synchronizationId).subscribe({
      next: (synchronization) => {
        this.loading.set(false);
        if (synchronization) {
          this.synchronizationData.set(synchronization);
        }
      },
      error: (error) => {
        this.loading.set(false);
        // Show server error message in toast
        const errorMessage = extractErrorMessage(error);
        const errorTitle = getErrorTitle(error);
        const errorSeverity = getErrorSeverity(error);

        this.messageService.add({
          severity: errorSeverity,
          summary: errorTitle,
          detail: errorMessage,
          life: 3000
        });

      },
    });
  }
}
